import React, { useEffect, useRef } from 'react';
import { ArrowUpRight, Sparkles, BookOpen } from 'lucide-react';
import substackPostsData from '../substackPosts.json';

type Accent = 'blush' | 'sun' | 'sky';

interface Project {
  title: string;
  description: string;
  tags: string[];
  image?: string;
  link?: string;
  date?: string;
  substack?: boolean;
  source?: string;
}

const accents: Record<
  Accent,
  { bar: string; chip: string; link: string; featuredBg: string; featuredBorder: string; panel: string; iconBg: string; label: string }
> = {
  blush: {
    bar: 'bg-blush-400',
    chip: 'bg-blush-50 text-ink-600 border-blush-100',
    link: 'text-blush-500',
    featuredBg: 'from-blush-50 via-background-50 to-sun-50',
    featuredBorder: 'border-blush-100',
    panel: 'from-blush-100 to-blush-200',
    iconBg: 'bg-blush-400',
    label: 'text-blush-500',
  },
  sun: {
    bar: 'bg-sun-400',
    chip: 'bg-sun-50 text-ink-600 border-sun-100',
    link: 'text-sun-500',
    featuredBg: 'from-sun-50 via-background-50 to-sky-50',
    featuredBorder: 'border-sun-100',
    panel: 'from-sun-100 to-sun-200',
    iconBg: 'bg-sun-400',
    label: 'text-sun-500',
  },
  sky: {
    bar: 'bg-sky-400',
    chip: 'bg-sky-50 text-ink-600 border-sky-100',
    link: 'text-sky-500',
    featuredBg: 'from-sky-50 via-background-50 to-blush-50',
    featuredBorder: 'border-sky-100',
    panel: 'from-sky-100 to-sky-200',
    iconBg: 'bg-sky-400',
    label: 'text-sky-500',
  },
};

/* Wraps card content in an external <a> when it links out, otherwise a <div>. */
const CardShell: React.FC<{ link?: string; className: string; children: React.ReactNode }> = ({
  link,
  className,
  children,
}) =>
  link ? (
    <a href={link} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  ) : (
    <div className={className}>{children}</div>
  );

/* ---------- Featured card (big, one per pillar) ---------- */
const FeaturedCard: React.FC<{ project: Project; accent: Accent }> = ({ project, accent }) => {
  const a = accents[accent];
  return (
    <CardShell
      link={project.link}
      className={`group block rounded-3xl overflow-hidden border ${a.featuredBorder} bg-gradient-to-br ${a.featuredBg} shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="grid md:grid-cols-2 items-stretch">
        {/* Copy */}
        <div className="p-8 md:p-10 order-2 md:order-1 flex flex-col justify-center">
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${a.label} mb-4`}>
            <Sparkles className="w-3.5 h-3.5" /> Featured
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-ink-800 tracking-tight mb-3">
            {project.title}
          </h3>
          <p
            className="text-ink-600 leading-relaxed mb-6 max-w-md"
            dangerouslySetInnerHTML={{ __html: project.description }}
          />
          {project.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {project.tags.map((tag) => (
                <span key={tag} className={`px-3 py-1 rounded-full text-xs font-medium border ${a.chip}`}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          {project.link && (
            <span className={`inline-flex items-center gap-1 font-semibold ${a.link} group-hover:gap-2 transition-all`}>
              Visit <ArrowUpRight className="w-4 h-4" />
            </span>
          )}
        </div>

        {/* Visual */}
        <div className="order-1 md:order-2 h-56 md:h-full min-h-[18rem] overflow-hidden">
          {project.image ? (
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${a.panel} flex items-center justify-center`}>
              <div className={`w-16 h-16 rounded-2xl ${a.iconBg} flex items-center justify-center shadow-md`}>
                <Sparkles className="w-8 h-8 text-white" />
              </div>
            </div>
          )}
        </div>
      </div>
    </CardShell>
  );
};

/* ---------- Small card (carousel item) ---------- */
const MiniCard: React.FC<{ project: Project; accent: Accent }> = ({ project, accent }) => {
  const a = accents[accent];
  return (
    <CardShell
      link={project.link}
      className="snap-start shrink-0 w-72 group flex flex-col rounded-2xl overflow-hidden bg-white border border-ink-100 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="relative aspect-[16/9] bg-background-200 overflow-hidden">
        {project.image ? (
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${a.panel} flex items-center justify-center text-2xl`}>
            {project.substack ? (
              <BookOpen className="w-7 h-7 text-white/90" />
            ) : (
              <Sparkles className="w-7 h-7 text-white/90" />
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col flex-grow p-4">
        {(project.substack || project.source || project.date) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1.5">
            {(project.substack || project.source) && (
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${a.label}`}>
                {project.substack ? 'Substack' : project.source}
              </span>
            )}
            {project.date && <span className="text-[11px] text-ink-400">{project.date}</span>}
          </div>
        )}
        <h4 className="text-base font-bold text-ink-800 mb-1 leading-snug">{project.title}</h4>
        <p
          className="text-ink-600 text-sm leading-relaxed mb-3"
          dangerouslySetInnerHTML={{ __html: project.description }}
        />
        {project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2 mt-auto">
            {project.tags.slice(0, 4).map((tag) => (
              <span key={tag} className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${a.chip}`}>
                {tag}
              </span>
            ))}
          </div>
        )}
        {project.link && (
          <span className={`inline-flex items-center gap-1 text-sm font-semibold ${a.link} ${project.tags.length === 0 ? 'mt-auto' : ''}`}>
            {project.substack ? 'Read post' : 'Read more'} <ArrowUpRight className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
    </CardShell>
  );
};

/* ---------- Auto-scrolling carousel ---------- */
const Carousel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const dirRef = useRef(1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let startTimer = 0;

    const step = () => {
      if (!pausedRef.current) {
        const max = el.scrollWidth - el.clientWidth;
        if (max > 2) {
          if (el.scrollLeft >= max - 1) dirRef.current = -1;
          else if (el.scrollLeft <= 0) dirRef.current = 1;
          el.scrollLeft += dirRef.current * 0.5;
        }
      }
      raf = requestAnimationFrame(step);
    };

    // Begin auto-scroll 10 seconds after load.
    startTimer = window.setTimeout(() => {
      raf = requestAnimationFrame(step);
    }, 10000);

    return () => {
      clearTimeout(startTimer);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
      onTouchStart={() => (pausedRef.current = true)}
      className="flex gap-4 overflow-x-auto pb-4 snap-x scroll-smooth"
      style={{ scrollbarWidth: 'thin' }}
    >
      {children}
    </div>
  );
};

/* ---------- Pillar ---------- */
const Pillar: React.FC<{
  id: string;
  title: string;
  blurb: string;
  accent: Accent;
  featured: Project;
  more: Project[];
}> = ({ id, title, blurb, accent, featured, more }) => {
  const a = accents[accent];
  return (
    <div id={id} className="scroll-mt-20">
      <div className="mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-ink-800 tracking-tight mb-3">{title}</h2>
        <div className={`w-16 h-1 rounded-full ${a.bar} mb-3`} />
        <p className="text-ink-600 max-w-2xl">{blurb}</p>
      </div>

      <FeaturedCard project={featured} accent={accent} />

      {more.length > 0 && (
        <div className="mt-8">
          <p className="text-sm font-medium text-ink-400 mb-3">More work (scroll to explore)</p>
          <Carousel>
            {more.map((p) => (
              <MiniCard key={p.title} project={p} accent={accent} />
            ))}
          </Carousel>
        </div>
      )}
    </div>
  );
};

/* ========================= Project data ========================= */

/* ---- Healthcare ---- */
const HEALTHCARE_FEATURED: Project = {
  title: 'Preserving Access to Medi-Cal',
  description:
    "Developing metrics for the Stanford CERC Impact Accelerator's initiative to pilot vendor solutions for improving the Medi-Cal redetermination process.",
  tags: ['Metric Development & Tracking', 'Medicaid'],
  image: '/IA-launch-lookbook-082.jpg',
};

const HEALTHCARE_MORE: Project[] = [
  {
    title: 'Palliative Care Program Design',
    description: 'Designed the care-management model and quality-monitoring strategy for a new community-based palliative care program for a State health department.',
    tags: ['Medicaid', 'Program Design', 'Care Mgmt', 'Journey Mapping'],
    image: '/healthcare-palliative.jpg',
  },
  {
    title: 'MS Brand Growth Strategy',
    description: 'Fielded and analyzed two surveys of 100+ providers to find a path to ~17K additional annual prescriptions for a large biopharma client.',
    tags: ['Growth Strategy', 'Go-to-Market', 'User Research', 'Survey Analysis'],
    image: '/healthcare-ms.jpg',
  },
  {
    title: 'SaaS Portfolio Transformation',
    description: 'Designed a new software delivery operating model to rationalize a portfolio of ~200 SaaS products for a public-sector healthcare client.',
    tags: ['Operating Model', 'Product Strategy', 'Stakeholder Mgmt'],
    image: '/healthcare-saas.png',
  },
  {
    title: 'Hospital Vendor Cost Optimization',
    description: 'Ran a competitive orthopedic-implant procurement across 13 categories and 18 vendors to deliver ~$20M in savings for a leading hospital system.',
    tags: ['Competitive Analysis', 'RFP Mgmt', 'Data-driven Decisions'],
    image: '/healthcare-hospital.jpg',
  },
  {
    title: 'MedTech Supply Chain Transformation',
    description: 'Developed six initiatives to cut backorders and lift inventory turnover, achieving ~$100M in savings for a large MedTech client.',
    tags: ['Project Mgmt', 'Business Cases'],
    image: '/healthcare-medtech.jpg',
  },
  {
    title: 'Covid-19 Test Supply Chain Management',
    description: 'Forecasted burn rate of test-kit components from weekly shipment data to help manage allocation of ~$12B in Covid-19 funding.',
    tags: ['Metric Tracking', 'Alteryx', 'SQL'],
    image: '/healthcare-covid.jpg',
  },
];

/* ---- Product & Design ---- */
const DESIGN_FEATURED: Project = {
  title: 'Reachly, your digital networking assistant',
  description:
    'An AI-powered tool for managing relationships with professional contacts. I designed and built it end to end, from research and UX to a shipped product.',
  tags: ['User Experience', 'React', 'Supabase', 'Google Cloud'],
  link: 'https://reachly.sarahzhou.com',
  image: '/design-reachly.png',
};

const DESIGN_MORE: Project[] = [
  {
    title: 'Stanford Alpine Journal',
    description: "Designer for the Stanford Alpine Club's annual journal.",
    tags: ['Editorial', 'Graphic Design'],
    image: '/SAJ_2026_mockup.jpg',
  },
  {
    title: 'Non-disclosure',
    description: "Editor and designer for Stanford GSB's student magazine.",
    tags: ['Editorial', 'Graphic Design'],
    image: '/non-disclosure.svg',
    link: 'https://medium.com/non-disclosure',
  },
  {
    title: 'Drone Control Interface',
    description: 'Led a team of five to design the UI for controlling drones in search-and-rescue, published in a peer-reviewed proceedings.',
    tags: ['User Experience', 'User Research', 'Usability Testing', 'Figma'],
    link: 'https://journals.sagepub.com/doi/abs/10.1177/1071181321651130',
    image: '/design-drone.jpg',
  },
  {
    title: 'UI Design Course Instructor',
    description: 'Co-developed and taught a week-long user-interface design course for high-school students.',
    tags: ['UI Design', 'Teaching', 'Graphic Design'],
    image: '/design-circle.jpg',
  },
  {
    title: 'Select Coursework',
    description: '• Human-Machine Interface<br/>• Systems Design<br/>• Design Thinking for Innovation<br/>• Digital Product Management',
    tags: [],
    image: '/design-coursework.png',
  },
];

/* ---- Writing ---- */
const WRITING_FEATURED: Project = {
  title: 'sarah’s summaries',
  description:
    'My substack. Join me on a journey through the US healthcare system to understand how we can better care for underserved communities.',
  tags: ['Substack', 'Care Delivery', 'Health Equity'],
  link: 'https://substack.com/@szhou27/posts',
  image: '/sarahssummaries-header.jpg',
};

const WRITING_ARTICLES: Project[] = [
  {
    title: 'Bringing More Asian Americans into the C-Suite',
    description: 'Wrote and analyzed a survey of 1,000+ corporate workers on Asian employees’ experiences in U.S. workplaces.',
    tags: ['AAPI', 'Survey Analysis'],
    source: 'BCG',
    date: 'Aug 2023',
    link: 'https://www.bcg.com/publications/2023/how-companies-can-promote-asian-american-executives',
    image: '/aapi.jpeg',
  },
  {
    title: 'Herding Rockets: Space Traffic Management',
    description: 'Forecasted satellites in orbit and potential value lost to collision risk by 2030.',
    tags: ['Aerospace', 'Market Sizing'],
    source: 'Space News',
    date: 'May 2022',
    link: 'https://spacenews.com/op-ed-herding-rockets-improved-space-traffic-management-will-accelerate-industry-growth/',
    image: '/space.jpg',
  },
  {
    title: 'Aversive Conditioning in Tardigrades',
    description: 'Developed an aversive-conditioning method for tardigrades to study how metabolism affects learning and memory.',
    tags: ['Neuroscience', 'Learning & Memory'],
    source: 'J Exp Psychol Anim Learn Cogn',
    date: 'Aug 2019',
    link: 'https://pubmed.ncbi.nlm.nih.gov/31368766/',
    image: '/tardigrade.jpg',
  },
];

// Substack posts are baked in at build time by scripts/fetch-substack.js
// (see the `prebuild` npm script), so no runtime fetch or CORS proxy is needed.
const substackPosts = substackPostsData as Project[];
const WRITING_MORE: Project[] = [...substackPosts, ...WRITING_ARTICLES];

const Projects: React.FC = () => (
  <section id="projects" className="py-16 bg-background-50">
    <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 space-y-24">
      <Pillar
        id="healthcare"
        title="Healthcare Strategy & BizOps"
        blurb="Consulting and operating work across government programs, hospital systems, MedTech, and biopharma."
        accent="blush"
        featured={HEALTHCARE_FEATURED}
        more={HEALTHCARE_MORE}
      />
      <Pillar
        id="design"
        title="Product & Design"
        blurb="Human-centered design and product work, from interface design and research to teaching and editorial design."
        accent="sun"
        featured={DESIGN_FEATURED}
        more={DESIGN_MORE}
      />
      <Pillar
        id="writing"
        title="Writing"
        blurb="Published articles and research, plus ongoing writing on healthcare."
        accent="sky"
        featured={WRITING_FEATURED}
        more={WRITING_MORE}
      />
    </div>
  </section>
);

export default Projects;
